/**
 * The realm's chronicle — the modal that answers `campaign.pendingEvent`.
 *
 * The strategic twin of `showChronicle` in `hud.ts`, and it holds to the same
 * two rules: there is no dismiss and Escape does not close it, because the
 * realm is frozen on the question and the only thing that unfreezes it is an
 * answer; and a choice the treasury cannot cover is shown, greyed, with the
 * price on it, because seeing the door you cannot afford is part of the
 * decision.
 *
 * One rule is its own, and it is the reason this is a file rather than another
 * `hud.ts` method: **the modal only blocks the world map.** `chronicleturn.ts`
 * is explicit that a pending question suspends the realm and *not* the town —
 * the masons do not down tools because the crown is being asked about an envoy.
 * So on the city screen this renders as a banner beside the travel bar instead,
 * and clicking it walks the player to the map, where the question is waiting.
 *
 * Everything drawn here comes out of `realmEventView` — resolved rules text,
 * prices, affordability — and everything sent back is one command. The UI never
 * touches the deck and never re-derives a context; that is the seam the sim
 * published, and holding to it is what lets a save taken with this open put it
 * straight back up.
 */

import { cmd, Cmd, type Command } from '../sim/commands.ts';
import type { World } from '../sim/world.ts';
import { realmEventView } from '../sim/campaign/chronicleturn.ts';
import { KEY_CONSOLE, KEY_PAUSE, KEY_MUTE, KEY_SAVE, KEY_LOAD, KEY_STATS, KEY_SLOWER, KEY_FASTER } from './keys.ts';

export interface RealmEventCallbacks {
  /** The answer, as a command for the realm's queue wherever the player stands. */
  onCommand(c: Command): void;
  /** The banner was clicked from the city: open the map, where the modal is. */
  onToRealm(): void;
}

/** Shared settlement/realm choice controls; state remains owned by the sim. */
export interface EventDialogModel {
  key: string;
  title: string;
  subtitle: string;
  flavour: string;
  waiting?: string;
  choices: Array<{ index: number; label: string; rules: string; price: string; affordable: boolean }>;
}

export class EventChoiceDialog {
  private model: EventDialogModel | null = null;
  private sheet: HTMLElement | null = null;
  private submitted = -1;
  private priorFocus: HTMLElement | null = null;
  private refusal = '';
  private choiceShape = '';

  constructor(private modal: HTMLElement, private choose: (index: number) => void | boolean) {
    injectStyle();
    modal.addEventListener('keydown', e => {
      if (!this.isOpen) return;
      if ([KEY_CONSOLE, KEY_PAUSE, KEY_MUTE, KEY_SAVE, KEY_LOAD, KEY_STATS, KEY_SLOWER, KEY_FASTER, '=', '_'].includes(e.key.toLowerCase())) return;
      e.stopPropagation();
      if (e.key === 'Escape') { e.preventDefault(); return; }
      if (e.key !== 'Tab') return;
      const buttons = [...modal.querySelectorAll<HTMLButtonElement>('button:not(:disabled)')];
      const at = buttons.indexOf(document.activeElement as HTMLButtonElement);
      if (buttons.length === 0) { e.preventDefault(); this.sheet?.focus(); }
      else if (e.shiftKey && at <= 0) { e.preventDefault(); buttons.at(-1)?.focus(); }
      else if (!e.shiftKey && (at < 0 || at === buttons.length - 1)) { e.preventDefault(); buttons[0].focus(); }
    });
    for (const type of ['mousedown', 'click']) modal.addEventListener(type, e => {
      if (!this.isOpen) return;
      e.stopPropagation();
      if (type === 'mousedown' && e.target === modal) e.preventDefault();
    });
  }

  get isOpen(): boolean { return this.modal.classList.contains('show') && this.modal.classList.contains('chronicle-event'); }

  show(model: EventDialogModel): void {
    const shape = model.choices.map(c => c.index).join(',');
    const sameEvent = model.key === this.model?.key;
    const changed = !sameEvent || shape !== this.choiceShape;
    const scrollTop = sameEvent ? this.sheet?.scrollTop ?? 0 : 0;
    this.choiceShape = shape;
    const opening = !this.isOpen;
    if (changed) { this.submitted = -1; this.refusal = ''; }
    this.model = model;
    if (this.submitted >= 0 && !model.choices.find(c => c.index === this.submitted)?.affordable) {
      this.submitted = -1;
      this.refusal = 'That choice is no longer affordable. Choose another response.';
    }
    if (changed || this.sheet !== this.modal.firstElementChild) {
      const titleId = `${this.modal.id}-event-title`;
      this.modal.innerHTML = `<div class="sheet" role="dialog" aria-modal="true" aria-labelledby="${titleId}" tabindex="-1">` +
        `<h1 id="${titleId}"></h1><div class="sub"></div><div class="cf"></div><div class="cards"></div>` +
        `<div class="event-status" role="status" aria-live="polite"></div></div>`;
      this.sheet = this.modal.firstElementChild as HTMLElement;
      const cards = this.sheet.querySelector('.cards')!;
      for (const choice of model.choices) {
        const button = document.createElement('button');
        button.type = 'button'; button.className = 'card'; button.dataset.choice = String(choice.index);
        button.innerHTML = '<span class="cn"></span><span class="cr"></span><span class="crar"></span><span class="event-lock"></span>';
        button.onclick = () => {
          const current = this.model?.choices.find(c => c.index === choice.index);
          if (this.submitted >= 0 || this.model?.waiting || !current?.affordable) return;
          this.submitted = choice.index;
          this.refresh();
          this.sheet?.focus({ preventScroll: true });
          try {
            if (this.choose(choice.index) === false) { this.submitted = -1; this.refresh(); }
          } catch (e) { this.submitted = -1; this.refresh(); throw e; }
        };
        cards.appendChild(button);
      }
      this.sheet.scrollTop = scrollTop;
    }
    this.modal.classList.add('show', 'chronicle-event');
    this.refresh();
    if (opening || changed) {
      if (opening && !this.modal.contains(document.activeElement)) this.priorFocus = document.activeElement as HTMLElement | null;
      this.sheet?.focus({ preventScroll: true });
    }
  }

  private refresh(): void {
    const model = this.model;
    if (!model || !this.sheet) return;
    const text = (selector: string, value: string) => {
      const el = this.sheet!.querySelector(selector)!;
      if (el.textContent !== value) el.textContent = value;
    };
    text('h1', model.title); text('.sub', model.subtitle); text('.cf', model.flavour);
    for (const choice of model.choices) {
      const button = this.sheet.querySelector<HTMLButtonElement>(`[data-choice="${choice.index}"]`)!;
      for (const [cls, value] of [['cn', choice.label], ['cr', choice.rules], ['crar', choice.price],
        ['event-lock', model.waiting ? 'Another crown must decide' : !choice.affordable ? 'Not enough resources' : '']]) {
        const el = button.querySelector(`.${cls}`)!;
        if (el.textContent !== value) el.textContent = value;
      }
      button.disabled = !!model.waiting || !choice.affordable || this.submitted >= 0;
      button.classList.toggle('locked', !choice.affordable || !!model.waiting);
      button.classList.toggle('chosen', this.submitted === choice.index);
    }
    text('.event-status', model.waiting || (this.submitted >= 0 ? 'Response sent. Waiting for confirmation…' : this.refusal || 'Choose one response to continue.'));
    this.sheet.setAttribute('aria-busy', String(this.submitted >= 0));
  }

  hide(clear = true): void {
    const restore = this.isOpen && this.modal.contains(document.activeElement);
    this.modal.classList.remove('show', 'chronicle-event');
    if (clear) { this.model = null; this.submitted = -1; }
    if (restore && this.priorFocus?.isConnected) this.priorFocus.focus({ preventScroll: true });
  }
}

export class RealmEventModal {
  private modal: HTMLElement;
  private note: HTMLButtonElement;
  private dialog: EventChoiceDialog;
  private realm: World | null = null;
  private pending: { id: string; turn: number; choices: string[]; context: string } | null = null;

  constructor(private cb: RealmEventCallbacks) {
    injectStyle();
    const root = document.getElementById('ui-root') as HTMLElement;
    this.modal = document.createElement('div'); this.modal.id = 'realmevent'; root.appendChild(this.modal);
    this.dialog = new EventChoiceDialog(this.modal, index => {
      const c = this.realm?.campaign, p = c?.pendingEvent;
      if (!c || !p || p.ctx.nation !== c.playerNation || p.id !== this.pending?.id || p.turn !== this.pending.turn ||
        p.choices[index] !== this.pending.choices[index] || JSON.stringify(p.ctx) !== this.pending.context) return false;
      const current = realmEventView(c)?.choices.find(ch => ch.index === index);
      if (!current?.affordable) return false;
      this.cb.onCommand(cmd(Cmd.CampaignAnswerEvent, index, 0, 0, 0, p.id));
      return true;
    });
    this.note = document.createElement('button'); this.note.type = 'button'; this.note.id = 'realmevent-note';
    this.note.style.display = 'none'; this.note.onclick = () => this.cb.onToRealm(); root.appendChild(this.note);
    this.note.addEventListener('keydown', e => e.stopPropagation());
  }

  get isOpen(): boolean { return this.dialog.isOpen; }

  update(realm: World | null, onMap: boolean): void {
    this.realm = realm;
    const c = realm?.campaign, p = c?.pendingEvent;
    this.pending = p ? { id: p.id, turn: p.turn, choices: [...p.choices], context: JSON.stringify(p.ctx) } : null;
    const view = c && p ? realmEventView(c) : null;
    if (!c || !p || !view) { this.dialog.hide(); this.note.style.display = 'none'; return; }
    const own = p.ctx.nation === c.playerNation;
    if (onMap) {
      this.note.style.display = 'none';
      this.dialog.show({ key: `${p.id}#${p.turn}#${p.ctx.nation}`, title: view.title,
        subtitle: `${view.kind} · the realm is waiting`, flavour: view.flavour, choices: view.choices,
        waiting: own ? '' : 'Waiting for the other crown to answer this event.' });
    } else {
      this.dialog.hide(false);
      const label = `${view.title}. ${own ? 'The council awaits your response.' : 'The council awaits another crown’s response.'} To the Realm`;
      if (this.note.textContent !== label) this.note.textContent = label;
      this.note.style.display = '';
    }
  }
}

let styled = false;
function injectStyle(): void {
  if (styled && document.getElementById('realmevent-style')) return;
  styled = true;
  const s = document.createElement('style');
  s.id = 'realmevent-style';
  s.textContent = `
    /* Above the world map (80) and its hover card (90). */
    #realmevent {
      position: absolute; inset: 0; z-index: 95; display: none;
      align-items: center; justify-content: center;
      background: rgba(6,6,7,0.82); pointer-events: auto;
    }
    #realmevent.show { display: flex; }
    #realmevent .sheet { width: min(920px, 92vw); max-height: 86vh; overflow-y: auto;
      background: var(--panel); border: 1px solid var(--panel-edge); padding: 22px 26px;
      box-shadow: 0 30px 90px rgba(0,0,0,0.8); }
    #realmevent h1 { font-size: 20px; letter-spacing: 0.26em; margin-bottom: 4px; }
    #realmevent .sub { font-size: 12px; color: var(--bone-deep); font-style: italic;
      margin-bottom: 18px; text-transform: capitalize; }
    #realmevent .sheet > .cf { font-size: 12.5px; font-style: italic; color: var(--bone-dim);
      line-height: 1.6; max-width: 72ch; margin-bottom: 18px; }
    #ui-root .chronicle-event .sheet { box-sizing: border-box; min-width: 0; width: min(920px, calc(100vw - 24px)); max-height: calc(100dvh - 32px); padding: 24px; }
    #ui-root .chronicle-event .cards { grid-template-columns: repeat(auto-fit, minmax(min(220px, 100%), 1fr)); }
    #ui-root .chronicle-event .card { display: block; min-width: 0; text-align: left; color: var(--bone); font: inherit; appearance: none; clip-path: none; border-radius: 4px; }
    #ui-root .chronicle-event .card span { display: block; }
    #ui-root .chronicle-event .card .cr { font-size: 13px; line-height: 1.6; }
    #ui-root .chronicle-event .card .crar { font-size: 12px; line-height: 1.5; letter-spacing: 0.025em; text-transform: none; color: var(--bone-dim); }
    #ui-root .chronicle-event .card.locked { opacity: 0.72; }
    #ui-root .chronicle-event .card:disabled { cursor: default; }
    #ui-root .chronicle-event .card:focus-visible { outline: 2px solid var(--gold); outline-offset: 3px; }
    #ui-root .chronicle-event .card.chosen { border-color: var(--gold); background: rgba(200,164,76,.15); }
    #ui-root .chronicle-event .event-lock { color: #e7a59a; font-size: 12px; margin-top: 8px; }
    #ui-root .chronicle-event .event-lock:empty, #ui-root .chronicle-event .crar:empty { display: none; }
    #ui-root .chronicle-event .event-status { margin-top: 18px; color: var(--bone-dim); font-size: 12px; line-height: 1.5; }
    #ui-root .chronicle-event .sheet:focus { outline: none; }
    @media (max-width: 600px) {
      #ui-root .chronicle-event .sheet { width: calc(100vw - 16px); padding: 16px; max-height: calc(100dvh - 16px); }
      #ui-root .chronicle-event h1 { font-size: 18px; line-height: 1.5; letter-spacing: .06em; }
      #ui-root .chronicle-event .cards { grid-template-columns: 1fr; gap: 10px; }
      #realmevent-note { left: 8px; right: 8px !important; top: 170px !important; max-width: none !important; }
    }
    /* The nudge on the city screen: under the travel bar, out of the HUD's way. */
    #realmevent-note { position: fixed; top: 88px; right: 288px; z-index: 40;
      background: rgba(16,15,13,0.92); border: 1px solid rgba(200,164,76,0.4);
      border-radius: 3px; padding: 8px 14px; cursor: pointer; pointer-events: auto; text-align: left;
      font-family: Inter, system-ui, sans-serif; font-size: 12px; color: var(--bone-dim);
      max-width: 420px; transition: border-color 120ms, background 120ms; }
    #realmevent-note:hover { background: rgba(200,164,76,0.14); border-color: var(--gold); }
    #realmevent-note b { color: var(--bone); font-weight: 500; }
    #realmevent-note .ren-mark { color: var(--gold); }
    #realmevent-note .ren-go { color: var(--gold); letter-spacing: 0.14em;
      text-transform: uppercase; font-size: 10px; margin-left: 6px; white-space: nowrap; }
  `;
  document.head.appendChild(s);
}
